/**
 * BroadcastAnalytics — modal showing open/click stats for one sent broadcast.
 *
 * Renders four KPI tiles, a 24-hour open histogram, a timezone-breakdown
 * panel (smart-send only), and a "top clicked links" table.
 */
import React, { useEffect, useState } from 'react';
import {
  X, BarChart3, MailOpen, MousePointerClick, Send, Globe2,
  TrendingUp, Loader2, AlertTriangle, ExternalLink, RefreshCw,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const HOUR_LABEL = (h) => {
  if (h === 0) return '12a';
  if (h === 12) return '12p';
  return h < 12 ? `${h}a` : `${h - 12}p`;
};

const KpiTile = ({ icon: Icon, label, value, sub, accent }) => (
  <div className={`bg-white border-2 rounded-lg p-3 ${accent}`}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4 text-slate-600" />
      <p className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">{label}</p>
    </div>
    <p className="text-2xl font-extrabold text-slate-900 leading-none">{value}</p>
    {sub && <p className="text-[11px] text-slate-500 mt-1">{sub}</p>}
  </div>
);

export default function BroadcastAnalytics({ adminToken, broadcastId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/admin/broadcasts/${broadcastId}/analytics`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      if (!res.ok) throw new Error(`Load failed (${res.status})`);
      setData(await res.json());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [broadcastId]);

  const stats = data?.stats || {};
  const histMax = data ? Math.max(1, ...Object.values(data.openHistogram || {})) : 1;

  return (
    <div
      className="fixed inset-0 z-[1010] bg-slate-900/60 backdrop-blur-sm flex items-stretch sm:items-center justify-center sm:p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="broadcast-analytics-modal"
    >
      <div className="bg-white w-full sm:max-w-4xl sm:rounded-2xl shadow-2xl flex flex-col max-h-screen sm:max-h-[92vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-teal-50">
          <div>
            <h2 className="text-lg font-extrabold text-emerald-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" /> Analytics
            </h2>
            <p className="text-xs text-emerald-800 mt-0.5 truncate max-w-[640px]">
              {data?.name || 'Loading…'}{data?.subject && <> · "{data.subject}"</>}
              {data?.sentAt && <> · sent {new Date(data.sentAt).toLocaleString()}</>}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              className="text-emerald-700 hover:text-emerald-900 p-2 rounded hover:bg-emerald-100"
              data-testid="broadcast-analytics-refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-800 p-1 rounded hover:bg-slate-100"
              data-testid="broadcast-analytics-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            </div>
          )}

          {err && (
            <div className="bg-rose-50 border border-rose-300 text-rose-900 text-sm rounded-lg p-3 flex items-start gap-2" data-testid="broadcast-analytics-error">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{err}</span>
            </div>
          )}

          {!loading && data && (
            <>
              {/* KPI tiles */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="broadcast-analytics-kpis">
                <KpiTile icon={Send} label="Delivered" value={stats.sent || 0}
                         sub={stats.failed ? `${stats.failed} failed` : 'no failures'}
                         accent="border-slate-200" />
                <KpiTile icon={MailOpen} label="Open rate" value={`${stats.openRate ?? 0}%`}
                         sub={`${stats.uniqueOpens || 0} unique · ${stats.totalOpens || 0} total opens`}
                         accent="border-emerald-200" />
                <KpiTile icon={MousePointerClick} label="Click rate" value={`${stats.clickRate ?? 0}%`}
                         sub={`${stats.uniqueClicks || 0} unique · ${stats.totalClicks || 0} total clicks`}
                         accent="border-amber-200" />
                <KpiTile icon={TrendingUp} label="Click-to-open" value={`${stats.clickToOpenRate ?? 0}%`}
                         sub={stats.clickToOpenRate >= 20 ? 'strong engagement'
                              : stats.clickToOpenRate >= 10 ? 'solid'
                              : stats.clickToOpenRate > 0  ? 'room to grow' : 'no clicks yet'}
                         accent="border-violet-200" />
              </div>

              {/* Open histogram */}
              <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
                <p className="text-sm font-extrabold text-slate-900 mb-3 flex items-center gap-1">
                  <BarChart3 className="w-4 h-4 text-slate-600" /> Opens by recipient's local hour
                </p>
                {Object.values(data.openHistogram || {}).every(v => v === 0) ? (
                  <p className="text-xs text-slate-500 py-6 text-center">No opens recorded yet. Check back in a few hours.</p>
                ) : (
                  <div className="flex items-end gap-1 h-32" data-testid="broadcast-analytics-histogram">
                    {Array.from({ length: 24 }, (_, h) => {
                      const n = data.openHistogram[h] || 0;
                      const heightPct = (n / histMax) * 100;
                      return (
                        <div key={h} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${HOUR_LABEL(h)}: ${n} open${n === 1 ? '' : 's'}`}>
                          <div className="text-[9px] text-slate-500 font-bold">{n || ''}</div>
                          <div
                            className="w-full bg-emerald-400 hover:bg-emerald-500 rounded-t transition-colors"
                            style={{ height: `${Math.max(heightPct, n > 0 ? 4 : 0)}%` }}
                          />
                          <div className="text-[9px] text-slate-400">{HOUR_LABEL(h)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-[10px] text-slate-500 mt-2">Hour is each recipient's *own* local hour (derived from practice state).</p>
              </div>

              {/* Top clicked links + timezone breakdown */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
                  <p className="text-sm font-extrabold text-slate-900 mb-2 flex items-center gap-1">
                    <MousePointerClick className="w-4 h-4 text-amber-600" /> Top clicked links
                  </p>
                  {(data.topLinks || []).length === 0 ? (
                    <p className="text-xs text-slate-500 py-4 text-center">No clicks recorded yet.</p>
                  ) : (
                    <table className="w-full text-xs" data-testid="broadcast-analytics-links">
                      <thead className="text-slate-500">
                        <tr>
                          <th className="text-left pb-1 font-bold">URL</th>
                          <th className="text-right pb-1 font-bold w-16">Clicks</th>
                          <th className="text-right pb-1 font-bold w-16">Unique</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.topLinks.map((l) => (
                          <tr key={l.linkId} className="border-t border-slate-100">
                            <td className="py-1.5 pr-2">
                              <a href={l.url} target="_blank" rel="noopener noreferrer"
                                 className="text-blue-700 hover:underline inline-flex items-center gap-1 truncate max-w-[280px]"
                                 title={l.url}>
                                {l.url}
                                <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              </a>
                            </td>
                            <td className="text-right font-bold text-slate-900">{l.totalClicks}</td>
                            <td className="text-right text-slate-700">{l.uniqueClicks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                <div className="bg-white border-2 border-slate-200 rounded-lg p-4">
                  <p className="text-sm font-extrabold text-slate-900 mb-2 flex items-center gap-1">
                    <Globe2 className="w-4 h-4 text-blue-600" /> Sends by timezone
                  </p>
                  {Object.keys(data.tzBreakdown || {}).length === 0 ? (
                    <p className="text-xs text-slate-500 py-4 text-center">
                      (Only available for Smart-Send broadcasts.)
                    </p>
                  ) : (
                    <ul className="space-y-1 text-xs" data-testid="broadcast-analytics-tz">
                      {Object.entries(data.tzBreakdown).sort((a, b) => b[1] - a[1]).map(([tz, n]) => (
                        <li key={tz} className="flex items-center justify-between border-b border-slate-100 pb-1">
                          <span className="text-slate-700">{tz}</span>
                          <span className="font-bold text-slate-900">{n}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
